import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { ErrorNote, Loading } from "../components/ui";
import { useLocations } from "../hooks";
import type {
  NotificationConfigT, PolicyConfigT, PolicyGroup, RunResult,
} from "../types";

const csv = (arr: string[]) => arr.join(", ");
const parseList = (s: string) => s.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);

export default function AttendancePolicies() {
  const locations = useLocations();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [groups, setGroups] = useState<PolicyGroup[]>([]);
  const [config, setConfig] = useState<PolicyConfigT | null>(null);
  const [notif, setNotif] = useState<NotificationConfigT | null>(null);

  // forms
  const [g, setG] = useState({ code: "", name: "", required_days_per_week: 4, biweekly_min_days: 8, monthly_min_days: 19, daily_min_hours: 7 });
  const [editing, setEditing] = useState(false);
  const [testTo, setTestTo] = useState("vijayravichandran@infoservices.com");
  const [run, setRun] = useState({ type: "daily", date: new Date().toISOString().slice(0, 10), month: new Date().toISOString().slice(0, 7), location: "", dry_run: true, send: false });
  const [runResult, setRunResult] = useState<RunResult | null>(null);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 4000); };

  const loadAll = () => {
    setLoading(true);
    Promise.all([api.policyGroups(), api.policyConfig(), api.policyNotifications()])
      .then(([gr, cfg, n]) => { setGroups(gr.groups); setConfig(cfg); setNotif(n); setError(null); })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(loadAll, []);

  const resetGroupForm = () => {
    setG({ code: "", name: "", required_days_per_week: 4, biweekly_min_days: 8, monthly_min_days: 19, daily_min_hours: 7 });
    setEditing(false);
  };
  const editGroup = (x: PolicyGroup) => {
    setG({
      code: x.code, name: x.name, required_days_per_week: x.required_days_per_week,
      biweekly_min_days: x.biweekly_min_days, monthly_min_days: x.monthly_min_days,
      daily_min_hours: x.daily_min_hours,
    });
    setEditing(true);
  };
  const saveGroup = async () => {
    if (!g.code.trim()) { setError("Group code is required."); return; }
    try { await api.savePolicyGroup(g); flash(editing ? "Group updated" : "Group saved"); resetGroupForm(); loadAll(); }
    catch (e) { setError((e as ApiError).message); }
  };
  const delGroup = async (code: string) => {
    if (!window.confirm(`Delete group ${code}?`)) return;
    try { await api.deletePolicyGroup(code); loadAll(); } catch (e) { setError((e as ApiError).message); }
  };
  const saveConfig = async () => {
    if (!config) return;
    try { const c = await api.savePolicyConfig(config); setConfig(c); flash("Config saved"); } catch (e) { setError((e as ApiError).message); }
  };
  const saveNotif = async () => {
    if (!notif) return;
    try { const n = await api.savePolicyNotifications(notif); setNotif(n); flash("Notifications saved"); } catch (e) { setError((e as ApiError).message); }
  };
  const testEmail = async () => {
    try { const r = await api.policyTestEmail(testTo); flash(`Test email: ${r.status}${r.error ? " — " + r.error : ""}`); }
    catch (e) { setError((e as ApiError).message); }
  };
  const doRun = async () => {
    try {
      const body: Record<string, unknown> = { type: run.type, location: run.location || undefined, dry_run: run.dry_run, send: run.send };
      if (run.type === "monthly") body.month = run.month; else body.date = run.date;
      const r = await api.policyRun(body); setRunResult(r);
      flash(`${run.dry_run ? "Dry-run" : "Run"} complete: ${r.summary.exceptions} exception(s)`);
      if (!run.dry_run) loadAll();
    } catch (e) { setError((e as ApiError).message); }
  };

  if (loading) return <Loading what="attendance policies" />;

  return (
    <div>
      <h1 className="h1">Attendance Policies</h1>
      <p className="sub">Define work-pattern groups, assign employees, configure thresholds, and send
        compliance alerts. Assessments use recorded office presence; absence/leave need HR reference data.</p>
      {error && <ErrorNote message={error} />}
      {msg && <div className="panel" style={{ borderColor: "var(--ok)", color: "var(--ok)" }}>{msg}</div>}

      {/* Groups */}
      <div className="panel">
        <h2>Policy groups</h2>
        <table><thead><tr><th>Code</th><th>Name</th><th>Days/wk</th><th>Days/2wk</th><th>Days/mo</th><th>Hrs/day</th><th>Automated</th><th></th></tr></thead>
          <tbody>
            {groups.map((x) => (
              <tr key={x.code} style={editing && g.code === x.code ? { background: "rgba(99,102,241,0.12)" } : undefined}><td>{x.code}</td><td>{x.name}</td><td>{x.required_days_per_week}</td>
                <td>{x.biweekly_min_days}</td><td>{x.monthly_min_days}</td><td>{x.daily_min_hours}</td><td>{x.automated ? "Yes" : "No"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="ghost" onClick={() => editGroup(x)}>Edit</button>
                  {!["fulltime", "hybrid", "exempt"].includes(x.code) && <button className="ghost" onClick={() => delGroup(x.code)}>Delete</button>}
                </td></tr>
            ))}
          </tbody></table>
        <h3 style={{ marginTop: 14, marginBottom: 4 }}>{editing ? `Edit group: ${g.code}` : "Add group"}</h3>
        <div className="filters">
          <div className="field"><label>Code</label><input value={g.code} readOnly={editing} onChange={(e) => setG({ ...g, code: e.target.value })} /></div>
          <div className="field"><label>Name</label><input value={g.name} onChange={(e) => setG({ ...g, name: e.target.value })} /></div>
          <div className="field"><label>Days/week</label><input type="number" value={g.required_days_per_week} onChange={(e) => setG({ ...g, required_days_per_week: +e.target.value })} /></div>
          <div className="field"><label>Days/2 weeks</label><input type="number" value={g.biweekly_min_days} onChange={(e) => setG({ ...g, biweekly_min_days: +e.target.value })} /></div>
          <div className="field"><label>Days/month</label><input type="number" value={g.monthly_min_days} onChange={(e) => setG({ ...g, monthly_min_days: +e.target.value })} /></div>
          <div className="field"><label>Hours/day</label><input type="number" value={g.daily_min_hours} onChange={(e) => setG({ ...g, daily_min_hours: +e.target.value })} /></div>
          <button className="primary" onClick={saveGroup}>{editing ? "Update group" : "Save group"}</button>
          {editing && <button className="ghost" onClick={resetGroupForm}>Cancel</button>}
        </div>
      </div>

      <div className="panel">
        <h2>Employee assignments</h2>
        <p className="notice">Assign employees to a work-mode group from <b>Employee Attendance</b> —
          select one or more employees, pick the work mode, and click Assign (or import a CSV there).
          Each employee's group shows in the <b>Group</b> column and on their profile.</p>
      </div>

      {/* Config */}
      {config && (
        <div className="panel">
          <h2>Policy configuration</h2>
          <div className="filters">
            <div className="field"><label>Daily min hours</label><input type="number" value={config.daily_min_hours} onChange={(e) => setConfig({ ...config, daily_min_hours: +e.target.value })} /></div>
            <div className="field"><label>Grace minutes</label><input type="number" value={config.grace_minutes} onChange={(e) => setConfig({ ...config, grace_minutes: +e.target.value })} /></div>
            <label className="field"><span>Daily enabled</span><input type="checkbox" checked={config.daily_enabled} onChange={(e) => setConfig({ ...config, daily_enabled: e.target.checked })} /></label>
            <label className="field"><span>Weekly enabled</span><input type="checkbox" checked={config.weekly_enabled} onChange={(e) => setConfig({ ...config, weekly_enabled: e.target.checked })} /></label>
            <label className="field"><span>Biweekly enabled</span><input type="checkbox" checked={config.biweekly_enabled} onChange={(e) => setConfig({ ...config, biweekly_enabled: e.target.checked })} /></label>
            <label className="field"><span>Monthly enabled</span><input type="checkbox" checked={config.monthly_enabled} onChange={(e) => setConfig({ ...config, monthly_enabled: e.target.checked })} /></label>
            <label className="field"><span>Has leave data</span><input type="checkbox" checked={config.has_leave_data} onChange={(e) => setConfig({ ...config, has_leave_data: e.target.checked })} /></label>
            <label className="field"><span>Has holiday data</span><input type="checkbox" checked={config.has_holiday_data} onChange={(e) => setConfig({ ...config, has_holiday_data: e.target.checked })} /></label>
            <button className="primary" onClick={saveConfig}>Save config</button>
          </div>
        </div>
      )}

      {/* Notifications */}
      {notif && (
        <div className="panel">
          <h2>Notifications {notif.smtp_configured ? <span className="tag ok">SMTP ready</span> : <span className="tag incomplete">SMTP not configured</span>}</h2>
          <div className="filters">
            <div className="field"><label>Daily recipients</label><input style={{ width: 320 }} value={csv(notif.daily_recipients)} onChange={(e) => setNotif({ ...notif, daily_recipients: parseList(e.target.value) })} /></div>
            <div className="field"><label>Weekly recipients</label><input style={{ width: 320 }} value={csv(notif.weekly_recipients)} onChange={(e) => setNotif({ ...notif, weekly_recipients: parseList(e.target.value) })} /></div>
            <div className="field"><label>Biweekly recipients</label><input style={{ width: 320 }} value={csv(notif.biweekly_recipients)} onChange={(e) => setNotif({ ...notif, biweekly_recipients: parseList(e.target.value) })} /></div>
            <div className="field"><label>Monthly recipients</label><input style={{ width: 320 }} value={csv(notif.monthly_recipients)} onChange={(e) => setNotif({ ...notif, monthly_recipients: parseList(e.target.value) })} /></div>
            <div className="field"><label>CC</label><input value={csv(notif.cc)} onChange={(e) => setNotif({ ...notif, cc: parseList(e.target.value) })} /></div>
            <div className="field"><label>Subject prefix</label><input value={notif.subject_prefix} onChange={(e) => setNotif({ ...notif, subject_prefix: e.target.value })} /></div>
            <label className="field"><span>Daily alerts</span><input type="checkbox" checked={notif.daily_enabled} onChange={(e) => setNotif({ ...notif, daily_enabled: e.target.checked })} /></label>
            <label className="field"><span>Weekly alerts</span><input type="checkbox" checked={notif.weekly_enabled} onChange={(e) => setNotif({ ...notif, weekly_enabled: e.target.checked })} /></label>
            <label className="field"><span>Biweekly alerts</span><input type="checkbox" checked={notif.biweekly_enabled} onChange={(e) => setNotif({ ...notif, biweekly_enabled: e.target.checked })} /></label>
            <label className="field"><span>Monthly alerts</span><input type="checkbox" checked={notif.monthly_enabled} onChange={(e) => setNotif({ ...notif, monthly_enabled: e.target.checked })} /></label>
            <button className="primary" onClick={saveNotif}>Save notifications</button>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <input style={{ width: 300 }} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test recipient" />
            <button className="ghost" onClick={testEmail}>Send test email</button>
          </div>
        </div>
      )}

      {/* Run */}
      <div className="panel">
        <h2>Run assessment</h2>
        <div className="filters">
          <div className="field"><label>Type</label>
            <select value={run.type} onChange={(e) => setRun({ ...run, type: e.target.value })}>
              <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="biweekly">Biweekly</option><option value="monthly">Monthly</option></select></div>
          {run.type === "monthly"
            ? <div className="field"><label>Month</label><input type="month" value={run.month} onChange={(e) => setRun({ ...run, month: e.target.value })} /></div>
            : <div className="field"><label>Date</label><input type="date" value={run.date} onChange={(e) => setRun({ ...run, date: e.target.value })} /></div>}
          <div className="field"><label>Location</label>
            <select value={run.location} onChange={(e) => setRun({ ...run, location: e.target.value })}>
              <option value="">All</option>{locations.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}</select></div>
          <label className="field"><span>Dry-run</span><input type="checkbox" checked={run.dry_run} onChange={(e) => setRun({ ...run, dry_run: e.target.checked, send: e.target.checked ? false : run.send })} /></label>
          <label className="field"><span>Send email</span><input type="checkbox" checked={run.send} disabled={run.dry_run} onChange={(e) => setRun({ ...run, send: e.target.checked })} /></label>
          <button className="primary" onClick={doRun}>{run.dry_run ? "Dry-run" : "Run"}</button>
        </div>
        {runResult && (
          <div style={{ marginTop: 12 }}>
            <div className="notice">Period {runResult.period} · assessed {runResult.summary.assessed} · exceptions {runResult.summary.exceptions} · review {runResult.summary.review} · not-eligible {runResult.summary.not_eligible}
              {runResult.email ? ` · email: ${runResult.email.status}` : ""}{runResult.dry_run ? " · (dry-run, nothing stored)" : ` · stored ${runResult.stored}`}</div>
            <p className="sub" style={{ marginTop: 6 }}>Per-employee results are sent to the configured recipients by email — not shown here.</p>
          </div>
        )}
      </div>
    </div>
  );
}
